import { useState } from "react";
import { Zap, Brain, Loader2, Check, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useBreakDown } from "@/hooks/useBreakDown";
import { useCreateSubtask } from "@/hooks/useSubtasks";
import { toast } from "sonner";
import type { Item } from "@/lib/types";
import { formatError } from "@/lib/utils";

type Props = {
  item: Item;
  onDone: () => void;
};

type Stage = "pick" | "loading" | "review" | "thinking";

export function BreakDownView({ item, onDone }: Props) {
  const [stage, setStage] = useState<Stage>("pick");
  const [subtasks, setSubtasks] = useState<string[]>([]);
  const [questions, setQuestions] = useState<string[]>([]);
  const [userContext, setUserContext] = useState("");
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const breakDown = useBreakDown();
  const createSubtask = useCreateSubtask();

  const handleQuickSplit = async () => {
    setStage("loading");
    try {
      const result = await breakDown.mutateAsync({ item_id: item.id, mode: "quick" });
      if (result.type === "subtasks") {
        setSubtasks(result.subtasks);
        setStage("review");
      }
    } catch (err) {
      toast.error(`Failed: ${formatError(err)}`);
      setStage("pick");
    }
  };

  const handleThink = async (context?: string) => {
    setStage("loading");
    try {
      const result = await breakDown.mutateAsync({
        item_id: item.id,
        mode: "think",
        user_context: context,
      });
      if (result.type === "questions") {
        setQuestions(result.questions);
        setStage("thinking");
      } else {
        setSubtasks(result.subtasks);
        setStage("review");
      }
    } catch (err) {
      toast.error(`Failed: ${formatError(err)}`);
      setStage("pick");
    }
  };

  const handleConfirm = async () => {
    const validSubtasks = subtasks.filter((s) => s.trim());
    if (validSubtasks.length === 0) return;

    try {
      for (const title of validSubtasks) {
        await createSubtask.mutateAsync({
          parent_id: item.id,
          title,
          project_tag: item.project_tag,
          date_target: item.date_target,
        });
      }
      toast(`Created ${validSubtasks.length} subtasks`);
      onDone();
    } catch (err) {
      toast.error(`Failed to create subtasks: ${formatError(err)}`);
    }
  };

  const removeSubtask = (idx: number) => {
    setSubtasks((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateSubtask = (idx: number, value: string) => {
    setSubtasks((prev) => prev.map((s, i) => (i === idx ? value : s)));
  };

  const addSubtask = () => {
    setSubtasks((prev) => [...prev, ""]);
    setEditingIdx(subtasks.length);
  };

  if (stage === "pick") {
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">Break it down</h4>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            className="flex flex-col items-center gap-1.5 h-auto py-4"
            onClick={handleQuickSplit}
          >
            <Zap className="h-5 w-5 text-yellow-500" />
            <span className="text-xs font-medium">Quick Split</span>
            <span className="text-[10px] text-muted-foreground">AI generates subtasks</span>
          </Button>
          <Button
            variant="outline"
            className="flex flex-col items-center gap-1.5 h-auto py-4"
            onClick={() => handleThink()}
          >
            <Brain className="h-5 w-5 text-purple-500" />
            <span className="text-xs font-medium">Help Me Think</span>
            <span className="text-[10px] text-muted-foreground">AI asks questions first</span>
          </Button>
        </div>
      </div>
    );
  }

  if (stage === "loading") {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Thinking...</span>
      </div>
    );
  }

  if (stage === "thinking") {
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">A couple questions first...</h4>
        <div className="space-y-2">
          {questions.map((q, i) => (
            <p key={i} className="text-sm bg-muted/50 rounded-lg px-3 py-2">
              {q}
            </p>
          ))}
        </div>
        <Textarea
          placeholder="Your answers..."
          value={userContext}
          onChange={(e) => setUserContext(e.target.value)}
          rows={3}
          className="text-sm"
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => handleThink(userContext)}
            disabled={!userContext.trim()}
          >
            Continue
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setStage("pick")}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  // Review stage
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground">
        Review subtasks ({subtasks.length})
      </h4>
      <div className="space-y-1.5">
        {subtasks.map((task, idx) => (
          <div key={idx} className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
            <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {editingIdx === idx ? (
              <Input
                value={task}
                onChange={(e) => updateSubtask(idx, e.target.value)}
                onBlur={() => setEditingIdx(null)}
                onKeyDown={(e) => e.key === "Enter" && setEditingIdx(null)}
                className="h-7 text-sm"
                autoFocus
              />
            ) : (
              <span
                className="flex-1 text-sm cursor-pointer"
                onClick={() => setEditingIdx(idx)}
              >
                {task || "(empty)"}
              </span>
            )}
            <button
              onClick={() => removeSubtask(idx)}
              className="text-muted-foreground hover:text-destructive shrink-0"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={addSubtask}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <Plus className="h-3 w-3" /> Add subtask
      </button>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={createSubtask.isPending || subtasks.filter((s) => s.trim()).length === 0}
        >
          {createSubtask.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : null}
          Create {subtasks.filter((s) => s.trim()).length} subtasks
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setStage("pick")}>
          Back
        </Button>
      </div>
    </div>
  );
}
